<script lang="ts" setup>
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import { languageLocale } from '@apps/main/modules/localization/localeUtils';

import { AppButton, AppLinkButton } from '@libs/ui';

import { StaySelectionType } from '../../models';
import type { HotelRoom, Stay } from '../../models';

const { t } = i18n.global;

interface HotelSelectionSectionProps {
  isHearted: boolean;
  isDateSelected: boolean;
  hotelName: string;
  stay: Stay;
  dateRange?: string;
}

const props = defineProps<HotelSelectionSectionProps>();

interface HotelSelectionSectionEmits {
  (event: 'select-hotel'): void;
  (event: 'unselect-hotel'): void;
  (event: 'scroll-to-rooms'): void;
  (event: 'continue-with-room'): void;
}

const emit = defineEmits<HotelSelectionSectionEmits>();

const browserStore = useBrowserStore();

const hasSelectedRoom = computed(
  () =>
    !!props.stay &&
    props.stay.selectionType === StaySelectionType.ManuallySelected
);

const totalAdt = computed<number>(() => {
  let total = 0;

  total = props.stay.rooms.reduce(
    (acc, room) => acc + room.pax.numberOfAdults,
    total
  );

  return total;
});

const totalChd = computed<number>(() => {
  let total = 0;

  total = props.stay.rooms.reduce(
    (acc, room) => acc + (room.pax.childAges?.length || 0),
    0
  );

  return total;
});

const shouldShowScrollToRoomButton = computed<boolean>(
  () =>
    props.isHearted &&
    (props.stay.selectionType === StaySelectionType.Suggested ||
      props.stay.selectionType === StaySelectionType.None)
);

function getLabelForRoomPax(room: HotelRoom): string {
  const nights = props.stay.numberOfNights || 1;
  return `${props.stay.numberOfNights} ${t('planner.nights', nights)}, ${
    room.paxString
  }`;
}

function getLabelForPaxSummary(): string {
  const childLabel = totalChd.value
    ? `, ${totalChd.value} ${t('planner.pax.children', totalChd.value)}`
    : '';

  const nights = props.stay.numberOfNights || 1;

  return `${props.stay.numberOfNights} ${t('planner.nights', nights)}, ${
    totalAdt.value
  } ${t('planner.pax.adults', totalAdt.value)} ${childLabel}`;
}
const displayLongTextStyle = computed(
  () =>
    (languageLocale === 'de' ||
      languageLocale === 'fr' ||
      languageLocale === 'es') &&
    browserStore.isSmallerThan1536px &&
    browserStore.isLargerThan1199px
);
</script>

<template>
  <div class="hotel-selection-section">
    <div v-if="hasSelectedRoom" class="selected-room">
      <div v-for="room in props.stay.rooms" :key="room.name" class="room">
        <p class="room-name">
          {{ room.name }}
        </p>
        <p class="room-stay">
          {{ getLabelForRoomPax(room) }}
        </p>
      </div>

      <div class="change-room">
        <AppLinkButton
          size="m"
          :bold="true"
          :text="$t('planner.change_room', props.stay.rooms.length || 1)"
          @click="emit('scroll-to-rooms')"
        />
      </div>
    </div>

    <div v-else class="flexible-mode">
      <span v-if="props.isHearted">
        {{ $t('planner.select_rooms_in_hotel', [props.hotelName]) }}
      </span>
      <span v-else>
        {{ $t('planner.select_hotel_without_rooms', [props.hotelName]) }}
      </span>
    </div>

    <div class="price-section">
      <div class="summary">
        <p>
          <span v-if="hasSelectedRoom">{{ $t('planner.total') }}</span>
          <span v-else>
            {{ getLabelForPaxSummary() }}
          </span>
        </p>
        <p v-if="props.isDateSelected" class="date-range">
          {{ props.dateRange }}
        </p>
      </div>

      <div v-if="props.stay.totalPrice" class="price">
        <span v-if="!hasSelectedRoom" class="price-from">
          {{ $t('planner.from') }}
        </span>
        <span class="price-value">
          {{ props.stay.totalPrice }}
        </span>
      </div>
    </div>

    <div class="cta-section" :class="{ 'is-long-text': displayLongTextStyle }">
      <div v-if="!browserStore.isSmallerThan1200px" class="button-wrapper">
        <AppButton
          v-if="props.isHearted"
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('unselect-hotel')"
        >
          {{ $t('planner.deselect_hotel') }}
        </AppButton>

        <AppButton
          v-if="!props.isHearted"
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('select-hotel')"
        >
          {{ $t('planner.select_hotel') }}
        </AppButton>
      </div>

      <div class="button-wrapper">
        <AppButton
          v-if="!props.isHearted"
          type="important"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('scroll-to-rooms')"
        >
          {{
            $t('planner.select_hotel_with_rooms', props.stay.rooms.length || 1)
          }}
        </AppButton>

        <AppButton
          v-if="shouldShowScrollToRoomButton"
          type="important"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('scroll-to-rooms')"
        >
          {{ $t('planner.select_rooms', props.stay.rooms.length || 1) }}
        </AppButton>

        <AppButton
          v-if="
            props.isHearted &&
            props.stay.selectionType === StaySelectionType.ManuallySelected
          "
          type="important"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('continue-with-room')"
        >
          {{ $t('planner.continue') }}
        </AppButton>
      </div>

      <div v-if="browserStore.isSmallerThan1200px" class="button-wrapper">
        <AppButton
          v-if="props.isHearted"
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('unselect-hotel')"
        >
          {{ $t('planner.deselect_hotel') }}
        </AppButton>

        <AppButton
          v-if="!props.isHearted"
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('select-hotel')"
        >
          {{ $t('planner.select_hotel') }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-section {
  background: var(--color-white);
  padding: var(--spacing-16);
  border: 1px solid var(--color-neutral-40);
  border-radius: var(--border-radius-8);
  margin-bottom: var(--spacing-16);
  box-sizing: border-box;

  .flexible-mode {
    padding-bottom: var(--spacing-16);
    border-bottom: 1px solid var(--color-neutral-40);
    span {
      @include font-label-m-light;
    }
  }

  .selected-room {
    background-color: var(--color-neutral-10);
    border-radius: var(--border-radius-8);
    padding: var(--spacing-16);

    .room {
      &:not(:last-child) {
        margin-bottom: var(--spacing-8);
      }

      .room-name {
        @include font-label-m-bold;
        color: var(--color-neutral-70);
        margin-bottom: var(--spacing-8);
      }

      .room-stay {
        @include font-label-s-light;
      }
    }

    .change-room {
      margin-top: var(--spacing-8);
      display: flex;
      justify-content: flex-end;
    }
  }

  .price-section {
    display: flex;
    justify-content: space-between;
    padding: var(--spacing-16) 0;

    .summary {
      p {
        margin: 0;
      }
      span {
        @include font-label-m-light;
      }

      .date-range {
        @include font-body-m-bold;
      }
    }
    .price {
      display: flex;
      align-items: center;

      .price-from {
        @include font-label-m-light;
        margin-right: var(--spacing-8);
      }

      .price-value {
        @include font-label-l-bold;
        color: var(--color-neutral-70);
      }
    }
  }

  .cta-section {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-16);
    width: 100%;
    box-sizing: border-box;

    @include for-less-or-equal-than-599-px {
      flex-direction: column;
    }
    .button-wrapper {
      @include for-less-or-equal-than-599-px {
        width: 100%;
      }
      width: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .is-long-text {
    .button-wrapper:first-child {
      flex: 1.5;
    }
    .button-wrapper:last-child {
      flex: 2;
    }
  }
}
</style>
